import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Card, KpiCard, Badge, SlidePanel, PanelSection, SpecGrid, SpecCell, Modal, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useRole } from '@/hooks/useRole';
import styles from './Certificates.module.css';

interface CertificateInfo {
  domain: string;
  subject: string;
  issuer: string;
  notBefore: string;
  notAfter: string;
  daysRemaining: number;
  serial: string;
  status: 'valid' | 'expiring_soon' | 'expired';
  tenantId: string | null;
  tenantSlug: string | null;
  tenantCompanyName: string | null;
}

interface RenewalLog {
  domain: string;
  timestamp: string;
  success: boolean;
  output: string;
}

interface CertListResponse {
  certificates: CertificateInfo[];
  autoRenewal: { configured: boolean; schedule: string | null };
}

const statusLabel: Record<string, string> = { valid: '有効', expiring_soon: '期限間近', expired: '期限切れ' };
const statusVariant: Record<string, 'green' | 'amber' | 'red'> = { valid: 'green', expiring_soon: 'amber', expired: 'red' };

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function daysClass(days: number): string {
  if (days > 30) return styles.daysValid;
  if (days > 0) return styles.daysWarn;
  return styles.daysExpired;
}

export function Certificates() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useRole();
  const { data, loading, refetch } = useApi<CertListResponse>('/console/certificates');
  const { data: logsData } = useApi<{ logs: RenewalLog[] }>('/console/certificates/renewal-logs');

  const [selected, setSelected] = useState<CertificateInfo | null>(null);
  const [renewTarget, setRenewTarget] = useState<string | null>(null);
  const [renewing, setRenewing] = useState<string | null>(null);

  const certs = data?.certificates ?? [];
  const autoRenewal = data?.autoRenewal;
  const logs = logsData?.logs ?? [];

  const validCount = certs.filter(c => c.status === 'valid').length;
  const warnCount = certs.filter(c => c.status === 'expiring_soon').length;
  const expiredCount = certs.filter(c => c.status === 'expired').length;

  const kpis = [
    { label: '総証明書数', value: String(certs.length), delta: '', trend: 'flat' as const },
    { label: '有効', value: String(validCount), delta: '', trend: 'up' as const },
    { label: '期限間近', value: String(warnCount), delta: warnCount > 0 ? '要確認' : '', trend: warnCount > 0 ? 'down' as const : 'flat' as const },
    { label: '期限切れ', value: String(expiredCount), delta: expiredCount > 0 ? '要対応' : '', trend: expiredCount > 0 ? 'down' as const : 'flat' as const },
  ];

  const handleRenew = async (domain: string) => {
    setRenewTarget(null);
    setRenewing(domain);
    try {
      await api(`/console/certificates/${encodeURIComponent(domain)}/renew`, { method: 'POST' });
      toast('証明書を更新しました', 'success');
      refetch();
      if (selected?.domain === domain) {
        setSelected(prev => prev ? { ...prev, daysRemaining: 90, status: 'valid', notAfter: new Date(Date.now() + 90 * 86_400_000).toISOString() } : prev);
      }
    } catch {
      toast('更新に失敗しました', 'error');
    } finally {
      setRenewing(null);
    }
  };

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  return (
    <div className={styles.page}>
      {/* KPI */}
      <div className={styles.kpiRow}>
        {kpis.map(k => <KpiCard key={k.label} {...k} />)}
      </div>

      {/* 証明書一覧 */}
      <Card title="SSL証明書一覧">
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>テナント</th>
              <th className={styles.th}>ドメイン</th>
              <th className={styles.th}>有効期限</th>
              <th className={styles.th}>残日数</th>
              <th className={styles.th}>ステータス</th>
              <th className={styles.th}>発行者</th>
              <th className={styles.th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {certs.length === 0 ? (
              <tr><td className={styles.emptyRow} colSpan={7}>証明書がありません</td></tr>
            ) : certs.map(c => (
              <tr key={c.domain}>
                <td className={styles.td}>
                  {c.tenantCompanyName ? (
                    <button className={styles.tenantLink} onClick={() => navigate('/tenants')}>
                      {c.tenantCompanyName}
                    </button>
                  ) : (
                    <span className={styles.systemLabel}>(システム)</span>
                  )}
                </td>
                <td className={styles.td}><span className={styles.domainCell}>{c.domain}</span></td>
                <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontSize: '11.5px' }}>{formatDate(c.notAfter)}</td>
                <td className={styles.td}><span className={daysClass(c.daysRemaining)}>{c.daysRemaining}日</span></td>
                <td className={styles.td}><Badge variant={statusVariant[c.status]}>{statusLabel[c.status]}</Badge></td>
                <td className={styles.td} style={{ fontSize: '11px', color: 'var(--ink3)' }}>{c.issuer.split('CN=')[1] ?? c.issuer}</td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    <button className={styles.btnDetail} onClick={() => setSelected(c)}>詳細</button>
                    {isSuperAdmin && (
                      <button
                        className={styles.btnRenew}
                        disabled={renewing === c.domain}
                        onClick={() => setRenewTarget(c.domain)}
                      >
                        {renewing === c.domain ? '更新中...' : '更新'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* 自動更新 + 更新履歴 */}
      <div className={styles.grid2}>
        <Card title="自動更新設定">
          <div className={styles.cronInfo}>
            <div className={styles.cronRow}>
              <span className={styles.cronKey}>ステータス</span>
              <Badge variant={autoRenewal?.configured ? 'green' : 'red'}>
                {autoRenewal?.configured ? '有効' : '未設定'}
              </Badge>
            </div>
            {autoRenewal?.schedule && (
              <div className={styles.cronRow}>
                <span className={styles.cronKey}>スケジュール</span>
                <code className={styles.code}>{autoRenewal.schedule}</code>
              </div>
            )}
            <div className={styles.cronRow}>
              <span className={styles.cronKey}>方式</span>
              <code className={styles.code}>certbot --webroot</code>
            </div>
          </div>
        </Card>

        <Card title="更新履歴">
          <div className={styles.logList}>
            {logs.length === 0 ? (
              <div className={styles.emptyRow}>更新履歴がありません</div>
            ) : logs.slice(0, 5).map((l, i) => (
              <div key={i} className={styles.logEntry}>
                <span className={styles.logTime}>{formatDateTime(l.timestamp)}</span>
                <span className={styles.logDomain}>{l.domain}</span>
                <span className={l.success ? styles.logOk : styles.logFail}>
                  {l.success ? '成功' : '失敗'}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* 詳細パネル */}
      <SlidePanel open={!!selected} title="証明書詳細" onClose={() => setSelected(null)}>
        {selected && (
          <>
          <PanelSection title="基本情報">
            <SpecGrid>
              <SpecCell label="ドメイン">{selected.domain}</SpecCell>
              <SpecCell label="テナント">
                {selected.tenantCompanyName ?? '(システム)'}
              </SpecCell>
              <SpecCell label="サブジェクト">{selected.subject}</SpecCell>
              <SpecCell label="発行者">{selected.issuer}</SpecCell>
              <SpecCell label="シリアル番号">{selected.serial}</SpecCell>
              <SpecCell label="有効期間開始">{formatDate(selected.notBefore)}</SpecCell>
              <SpecCell label="有効期間終了">{formatDate(selected.notAfter)}</SpecCell>
              <SpecCell label="残り日数">
                <span className={daysClass(selected.daysRemaining)}>{selected.daysRemaining}日</span>
              </SpecCell>
              <SpecCell label="ステータス">
                <Badge variant={statusVariant[selected.status]}>{statusLabel[selected.status]}</Badge>
              </SpecCell>
            </SpecGrid>
          </PanelSection>
          {isSuperAdmin && (
            <div style={{ padding: '16px 20px' }}>
              <button
                className={styles.btnRenew}
                style={{ width: '100%', padding: '8px 0', fontSize: '13px' }}
                disabled={renewing === selected.domain}
                onClick={() => setRenewTarget(selected.domain)}
              >
                {renewing === selected.domain ? '更新中...' : '証明書を更新する'}
              </button>
            </div>
          )}
          </>
        )}
      </SlidePanel>

      {/* 更新確認モーダル */}
      <Modal
        open={!!renewTarget}
        title="SSL証明書の更新"
        onClose={() => setRenewTarget(null)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className={styles.btnDetail} onClick={() => setRenewTarget(null)}>キャンセル</button>
            <button className={styles.btnRenew} onClick={() => renewTarget && handleRenew(renewTarget)}>更新する</button>
          </div>
        }
      >
        <p style={{ fontSize: '13px', lineHeight: 1.6 }}>
          <code className={styles.code}>{renewTarget}</code> のSSL証明書を更新しますか？
        </p>
        <p style={{ fontSize: '11.5px', color: 'var(--ink3)', marginTop: 8 }}>
          Let's Encrypt のレート制限: 同一ドメインにつき週5回まで
        </p>
      </Modal>
    </div>
  );
}
